import { DBSentence } from '../models/db_models';

export interface WordChunk {
  chunk: string; // Original text including punctuation
  word: string;  // Just letters/digits
}

/**
 * Parse a sentence into word chunks, preserving punctuation
 * Based on WordChunkUtils.java from original Android app
 */
export const parseIntoWordChunks = (sentence: string): WordChunk[] => {
  // Normalize whitespace
  let normalized = sentence.replace(/\s+/g, ' ').trim();

  // Remove spaces before sentence-ending punctuation
  normalized = normalized.replace(/\s+([.?!])/g, '$1');

  // Split by whitespace
  const parts = normalized.split(/\s+/);
  const chunks: WordChunk[] = [];

  for (const chunk of parts) {
    // Extract word (letters/digits only)
    const word = chunk.replace(/[^a-zA-Z0-9\u00C0-\u024F\u0400-\u04FF\u0600-\u06FF\u4E00-\u9FFF]/g, '');

    if (word.length > 0) {
      chunks.push({ chunk, word });
    } else if (chunks.length > 0) {
      // If no word (pure punctuation), append to previous chunk
      chunks[chunks.length - 1].chunk += chunk;
    }
  }

  // If first chunk has no word, merge with second
  if (chunks.length > 1 && chunks[0].word.length === 0) {
    chunks[1].chunk = chunks[0].chunk + ' ' + chunks[1].chunk;
    chunks.shift();
  }

  return chunks;
};

/**
 * Generate answer options for a word
 * Returns array of 4 options (1 correct + 3 wrong answers)
 */
export const generateAnswerOptions = (
  correctWord: string,
  vocabularyPool: string[],
  answerCount: number = 4
): string[] => {
  // Remove correct answer from pool
  const wrongAnswers = vocabularyPool.filter(w => w !== correctWord);

  // Randomly select (answerCount - 1) wrong answers
  const shuffled = wrongAnswers.sort(() => Math.random() - 0.5);
  const selected = shuffled.slice(0, answerCount - 1);

  // Add correct answer
  const allAnswers = [...selected, correctWord];

  // Shuffle all answers
  return allAnswers.sort(() => Math.random() - 0.5);
};

/**
 * Build vocabulary pool from sentences
 * Extracts all unique words from target sentences
 */
export const buildVocabularyPool = (sentences: DBSentence[]): string[] => {
  const words = new Set<string>();

  for (const sentence of sentences) {
    const chunks = parseIntoWordChunks(sentence.targetSentence);
    for (const chunk of chunks) {
      if (chunk.word.length > 0) {
        words.add(chunk.word);
      }
    }
  }

  return Array.from(words);
};

/**
 * Calculate if quiz can be marked as done based on success rate
 * @param incorrectAnswers Number of incorrect answers
 * @param totalWords Total words in sentence
 * @param minCorrectPercent Minimum correct percentage (default 90)
 */
export const canBeMarkedAsDone = (
  incorrectAnswers: number,
  totalWords: number,
  minCorrectPercent: number = 90
): boolean => {
  const incorrectRatio = incorrectAnswers / totalWords;
  const threshold = 1 - (minCorrectPercent / 100);
  return incorrectRatio < threshold;
};

/**
 * Get known sentence alternatives (separated by |)
 */
export const getKnownSentenceAlternatives = (knownSentence: string): string[] => {
  return knownSentence.split('|').map(s => s.trim()).filter(s => s.length > 0);
};
